import { useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { router, usePage } from "@inertiajs/react"
import { useInertiaCable } from "@inertia-cable/react"
import {
  Crown, ShieldCheck, Headphones, Mail, Users,
  Search, ChevronDown, ChevronLeft, ChevronRight, Lock, MoreVertical, Plus,
} from "lucide-react"
import { AppShell } from "@/components/layout/app-shell"
import { PageHeader } from "@/components/layout/page-header"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { TeamMemberDrawer } from "@/components/domain/team-member-drawer"
import { InviteTeammateModal } from "@/components/domain/invite-teammate-modal"
import { fmtNumber } from "@/lib/format"
import { useCan } from "@/lib/authz"
import { cn } from "@/lib/cn"
import type { SharedProps, RoleKey } from "@/types/inertia"
import type { TeamMember, TeamPageProps, MemberStatus, TeamPagination } from "@/types/team"

// Settings → Team. KPI tile row → filter bar → data table → pagination.
// CompanyUser broadcasts_to(company), so any invite / role flip / disable
// from another seat re-flows here automatically via inertia_cable_stream.

const ROLE_FILTER_OPTIONS: { key: RoleKey | "all"; label: string }[] = [
  { key: "all",           label: "All roles" },
  { key: "super_admin",   label: "Super admin" },
  { key: "admin",         label: "Admin" },
  { key: "agent_manager", label: "Agent manager" },
  { key: "agent",         label: "Agent" },
]

const ROLE_TITLE: Record<RoleKey, string> = {
  super_admin:   "Super Administrator",
  admin:         "Administrator",
  agent_manager: "Manager",
  agent:         "Agent",
}

const ROLE_DROPDOWN_LABEL: Record<RoleKey, string> = {
  super_admin:   "Super admin",
  admin:         "Admin",
  agent_manager: "Manager",
  agent:         "Agent",
}

export default function SettingsTeamIndex({
  members,
  kpis,
  filter,
  pagination,
  team_stream,
  invitable_roles,
  agent_pools,
}: TeamPageProps) {
  const { props } = usePage<SharedProps>()
  const flash = props.flash
  const currentUserId = props.current_user?.id
  const can = useCan()

  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [search, setSearch] = useState(filter.q ?? "")
  const [inviteOpen, setInviteOpen] = useState(false)

  useInertiaCable(team_stream, {
    only: ["members", "kpis", "bucket_counts", "pagination"],
    debounce: 250,
  })

  const selectedMember = useMemo(
    () => members.find((m) => m.id === selectedId) ?? null,
    [members, selectedId],
  )

  function buildUrl(patch: Partial<{
    q: string | null; role: RoleKey | null; status: MemberStatus | null; page: number | null
  }>) {
    const merged = { ...filter, page: pagination.page, ...patch }
    const params = new URLSearchParams()
    if (merged.q)      params.set("q", merged.q)
    if (merged.role)   params.set("role", merged.role)
    if (merged.status) params.set("status", merged.status)
    if (merged.page && merged.page > 1) params.set("page", String(merged.page))
    const qs = params.toString()
    return qs ? `/settings/team?${qs}` : "/settings/team"
  }

  // Debounced search — fires 300ms after the user stops typing.
  const initialSearch = useRef(filter.q ?? "")
  useEffect(() => {
    if (search === initialSearch.current) return
    const handle = setTimeout(() => {
      const next = search.trim() || null
      router.visit(buildUrl({ q: next, page: 1 }), { preserveState: true, preserveScroll: true })
    }, 300)
    return () => clearTimeout(handle)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  function setRole(role: RoleKey | "all") {
    router.visit(buildUrl({ role: role === "all" ? null : role, page: 1 }), { preserveState: true, preserveScroll: true })
  }

  function setStatus(status: "all" | MemberStatus) {
    router.visit(buildUrl({ status: status === "all" ? null : status, page: 1 }), { preserveState: true, preserveScroll: true })
  }

  function gotoPage(p: number) {
    router.visit(buildUrl({ page: p }), { preserveState: true, preserveScroll: true })
  }

  return (
    <AppShell>
      <PageHeader
        eyebrow="Settings"
        title="Team"
        subtitle="Members, roles, and pending invitations."
        actions={
          can("team_invite") ? (
            <Button onClick={() => setInviteOpen(true)}>
              <Plus size={14} strokeWidth={2.5} className="mr-1" /> Invite teammate
            </Button>
          ) : undefined
        }
      />

      {flash.notice && (
        <div className="rounded-control border border-status-success/30 bg-status-success/10 px-4 py-2 text-sm text-status-success">
          {flash.notice}
        </div>
      )}
      {flash.alert && (
        <div className="rounded-control border border-status-error/30 bg-status-error/10 px-4 py-2 text-sm text-status-error">
          {flash.alert}
        </div>
      )}

      <div className="space-y-5">
        <KpiRow kpis={kpis} />

        <FilterBar
          search={search}
          setSearch={setSearch}
          role={filter.role}
          setRole={setRole}
          status={filter.status}
          setStatus={setStatus}
        />

        <section className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_380px]">
          <div className="space-y-4">
            <MemberTable
              members={members}
              selectedId={selectedId}
              onSelect={setSelectedId}
              currentUserId={currentUserId}
            />
            <Pagination pagination={pagination} onPageChange={gotoPage} />
          </div>
          <TeamMemberDrawer
            member={selectedMember}
            onClose={() => setSelectedId(null)}
            agentPools={agent_pools}
          />
        </section>
      </div>

      <InviteTeammateModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        invitableRoles={invitable_roles}
        agentPools={agent_pools}
      />
    </AppShell>
  )
}

// --- KPIs --------------------------------------------------------------

function KpiRow({ kpis }: { kpis: TeamPageProps["kpis"] }) {
  return (
    <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
      <KpiTile icon={<Users size={26} strokeWidth={1.5} />} iconTone="neutral"
        label="Total users" value={fmtNumber(kpis.total_users.value)} caption={kpis.total_users.caption} />
      <KpiTile icon={<Crown size={26} strokeWidth={1.5} />} iconTone="gold"
        label="Admins" value={fmtNumber(kpis.admins.value)} caption={kpis.admins.caption} />
      <KpiTile icon={<ShieldCheck size={26} strokeWidth={1.5} />} iconTone="blue"
        label="Managers" value={fmtNumber(kpis.agent_managers.value)} caption={kpis.agent_managers.caption} />
      <KpiTile icon={<Headphones size={26} strokeWidth={1.5} />} iconTone="green"
        label="Agents" value={fmtNumber(kpis.agents.value)} caption={kpis.agents.caption} />
      <KpiTile icon={<Mail size={26} strokeWidth={1.5} />} iconTone="neutral"
        label="Pending invites" value={fmtNumber(kpis.pending_invites.value)} caption={kpis.pending_invites.caption} />
    </section>
  )
}

function KpiTile({ icon, iconTone, label, value, caption }: {
  icon: React.ReactNode
  iconTone: "neutral" | "gold" | "blue" | "green"
  label: string
  value: string
  caption: string
}) {
  const toneClass = {
    neutral: "text-app-muted",
    gold:    "text-vip-gold",
    blue:    "text-status-info",
    green:   "text-status-success",
  }[iconTone]
  return (
    <Card className="p-5">
      <div className="flex items-start gap-3">
        <div className={cn("shrink-0", toneClass)}>{icon}</div>
        <div>
          <div className="text-sm font-medium text-app-muted">{label}</div>
          <div className="my-1 text-3xl font-semibold tracking-tight text-app-text">{value}</div>
          <div className="text-xs text-app-subtle">{caption}</div>
        </div>
      </div>
    </Card>
  )
}

// --- Filter bar --------------------------------------------------------

function FilterBar({
  search, setSearch, role, setRole, status, setStatus,
}: {
  search: string
  setSearch: (s: string) => void
  role: RoleKey | null
  setRole: (r: RoleKey | "all") => void
  status: MemberStatus | null
  setStatus: (s: MemberStatus | "all") => void
}) {
  return (
    <section className="flex flex-wrap items-center gap-3">
      <div className="relative min-w-[280px] flex-1">
        <Search size={14} strokeWidth={2} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-app-subtle" />
        <Input
          value={search}
          placeholder="Search by name or email…"
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>
      <SelectChip value={role ?? "all"} onChange={(v) => setRole(v as RoleKey | "all")}
        options={ROLE_FILTER_OPTIONS.map((r) => ({ value: r.key, label: r.label }))} />
      <SelectChip value={status ?? "all"} onChange={(v) => setStatus(v as MemberStatus | "all")}
        options={[
          { value: "all",      label: "All status" },
          { value: "active",   label: "Active" },
          { value: "pending",  label: "Pending" },
          { value: "disabled", label: "Disabled" },
        ]} />
    </section>
  )
}

function SelectChip({ value, onChange, options }: {
  value: string
  onChange: (v: string) => void
  options: { value: string; label: string }[]
}) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="appearance-none rounded-control border border-border bg-white/5 py-2 pl-3 pr-9 text-sm text-app-text outline-none focus:border-brand-red/50"
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <ChevronDown size={14} strokeWidth={2} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-app-subtle" />
    </div>
  )
}

// --- Table -------------------------------------------------------------

function MemberTable({ members, selectedId, onSelect, currentUserId }: {
  members: TeamMember[]
  selectedId: number | null
  onSelect: (id: number) => void
  currentUserId: number | undefined
}) {
  if (members.length === 0) {
    return (
      <Card variant="inset" className="p-6 text-sm text-app-muted">
        No members match this filter.
      </Card>
    )
  }
  return (
    <Card className="overflow-hidden p-0">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-white/[0.03] text-xs uppercase tracking-wide text-app-subtle">
            <th className="px-5 py-3 text-left font-medium">User</th>
            <th className="px-4 py-3 text-left font-medium">Email</th>
            <th className="px-4 py-3 text-left font-medium">Role</th>
            <th className="px-4 py-3 text-left font-medium">Status</th>
            <th className="px-4 py-3 text-left font-medium">Last login</th>
            <th className="px-4 py-3 text-left font-medium">Invited by</th>
            <th className="px-4 py-3 text-left font-medium">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {members.map((m) => (
            <MemberRow key={m.id}
              member={m}
              selected={selectedId === m.id}
              onSelect={onSelect}
              isMe={currentUserId === m.user_id}
            />
          ))}
        </tbody>
      </table>
    </Card>
  )
}

function MemberRow({ member: m, selected, onSelect, isMe }: {
  member: TeamMember
  selected: boolean
  onSelect: (id: number) => void
  isMe: boolean
}) {
  return (
    <tr
      onClick={() => onSelect(m.id)}
      className={cn(
        "cursor-pointer text-app-text transition",
        selected ? "bg-brand-redSoft/30" : "hover:bg-white/5",
      )}
    >
      <td className="px-5 py-3 align-middle">
        <div className="flex items-center gap-3">
          <RowAvatar member={m} />
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-app-text">{m.name}</span>
              {isMe && (
                <span className="rounded-chip border border-vip-gold/40 bg-vip-goldMute px-2 py-0.5 text-[10px] uppercase tracking-wide text-vip-goldSoft">
                  You
                </span>
              )}
            </div>
            <div className="text-xs text-app-subtle">{ROLE_TITLE[m.role]}</div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 align-middle text-app-muted">{m.email}</td>
      <td className="px-4 py-3 align-middle" onClick={(e) => e.stopPropagation()}>
        <RoleSelect member={m} />
      </td>
      <td className="px-4 py-3 align-middle"><StatusPill status={m.status} /></td>
      <td className="px-4 py-3 align-middle text-app-muted">
        <LastLoginCell iso={m.last_login_at} />
      </td>
      <td className="px-4 py-3 align-middle text-app-muted">{m.invited_by_name ?? "—"}</td>
      <td className="px-4 py-3 align-middle" onClick={(e) => e.stopPropagation()}>
        <ActionsCell member={m} />
      </td>
    </tr>
  )
}

function RowAvatar({ member }: { member: TeamMember }) {
  const [imgFailed, setImgFailed] = useState(false)
  const initial = (member.name.trim()[0] ?? "?").toUpperCase()
  if (member.avatar_url && !imgFailed) {
    return (
      <img src={member.avatar_url} alt={member.name} onError={() => setImgFailed(true)}
        className="h-10 w-10 shrink-0 rounded-full border border-border object-cover" />
    )
  }
  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand-red to-brand-redDeep text-sm font-semibold text-white shadow-redGlow">
      {initial}
    </div>
  )
}

function RoleSelect({ member: m }: { member: TeamMember }) {
  const editable = m.demotable_to.length > 0
  if (!editable) {
    return (
      <div className="inline-flex items-center justify-between gap-3 rounded-control border border-border bg-white/[0.03] px-3 py-1.5 text-sm text-app-muted">
        {ROLE_DROPDOWN_LABEL[m.role]}
        <Lock size={12} strokeWidth={2} className="text-app-subtle" />
      </div>
    )
  }
  function onChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const role = e.target.value as RoleKey
    if (role === m.role) return
    router.patch(`/settings/team/${m.id}`, { role }, { preserveScroll: true })
  }
  return (
    <div className="relative inline-block">
      <select
        value={m.role}
        onChange={onChange}
        className="appearance-none rounded-control border border-border bg-white/5 py-1.5 pl-3 pr-9 text-sm text-app-text outline-none focus:border-brand-red/50"
      >
        {m.demotable_to.includes(m.role) ? null : (
          <option value={m.role}>{ROLE_DROPDOWN_LABEL[m.role]}</option>
        )}
        {m.demotable_to.map((r) => <option key={r} value={r}>{ROLE_DROPDOWN_LABEL[r]}</option>)}
      </select>
      <ChevronDown size={12} strokeWidth={2} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-app-subtle" />
    </div>
  )
}

function StatusPill({ status }: { status: MemberStatus }) {
  if (status === "active") {
    return <span className="inline-flex items-center rounded-chip border border-status-success/30 bg-status-success/10 px-2.5 py-0.5 text-xs font-medium text-status-success">Active</span>
  }
  if (status === "pending") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-chip border border-status-warning/30 bg-status-warning/10 px-2.5 py-0.5 text-xs font-medium text-status-warning">
        <span className="h-1.5 w-1.5 rounded-full bg-status-warning" /> Pending invite
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-chip border border-status-error/30 bg-status-error/10 px-2.5 py-0.5 text-xs font-medium text-status-error">
      <span className="h-1.5 w-1.5 rounded-full bg-status-error" /> Disabled
    </span>
  )
}

function LastLoginCell({ iso }: { iso: string | null }) {
  if (!iso) return <span>—</span>
  const d = new Date(iso)
  const date = d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
  return (
    <div className="leading-tight">
      <div>{date}</div>
      <div className="text-xs text-app-subtle">{time}</div>
    </div>
  )
}

function ActionsCell({ member: m }: { member: TeamMember }) {
  // Inline button picks the most likely next action by lifecycle state.
  function primary(): React.ReactNode {
    if (m.status === "pending" || (m.status === "active" && !m.last_login_at)) {
      return (
        <Button variant="secondary" size="sm" onClick={() => router.post(`/settings/team/${m.id}/resend_invite`, {}, { preserveScroll: true })}>
          Resend invite
        </Button>
      )
    }
    if (m.status === "disabled") {
      return (
        <Button variant="secondary" size="sm" onClick={() => router.post(`/settings/team/${m.id}/enable`, {}, { preserveScroll: true })}>
          Enable
        </Button>
      )
    }
    return <span className="text-app-subtle">—</span>
  }

  function onSoftRemove() {
    if (!confirm(`Remove ${m.name} from the company? They'll be hidden from this list and their MFA + pools reset if you re-invite them later.`)) return
    router.post(`/settings/team/${m.id}/disable`, {}, { preserveScroll: true })
  }

  function onResetPassword() {
    if (!confirm(`Send a password reset email to ${m.email}?`)) return
    router.post(`/settings/team/${m.id}/reset_password`, {}, { preserveScroll: true })
  }

  return (
    <div className="flex items-center gap-1.5">
      {primary()}
      {m.can_remove && (
        <KebabMenu member={m} onSoftRemove={onSoftRemove} onResetPassword={onResetPassword} />
      )}
    </div>
  )
}

function KebabMenu({
  member: m,
  onSoftRemove,
  onResetPassword,
}: {
  member: TeamMember
  onSoftRemove: () => void
  onResetPassword: () => void
}) {
  const [open, setOpen] = useState(false)
  // Menu is portaled to <body> so the surrounding table card's
  // `overflow-hidden` (kept for rounded corners) can't clip it. We
  // compute fixed coords from the button's bounding rect at toggle time
  // and flip upward when the row sits near the viewport bottom.
  const [coords, setCoords] = useState<{ top: number; right: number; placement: "down" | "up" } | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const btnRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return
    function onClick(e: MouseEvent) {
      const t = e.target as Node
      if (menuRef.current?.contains(t)) return
      if (btnRef.current?.contains(t)) return
      setOpen(false)
    }
    document.addEventListener("mousedown", onClick)
    return () => document.removeEventListener("mousedown", onClick)
  }, [open])

  function toggle() {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom
      const placement: "down" | "up" = spaceBelow < 160 ? "up" : "down"
      setCoords({
        top:   placement === "down" ? rect.bottom + 4 : rect.top - 4,
        right: window.innerWidth - rect.right,
        placement,
      })
    }
    setOpen((v) => !v)
  }

  return (
    <>
      <button ref={btnRef} type="button" onClick={toggle}
        className="rounded-control p-1.5 text-app-muted hover:bg-white/10 hover:text-app-text">
        <MoreVertical size={16} strokeWidth={2} />
      </button>
      {open && coords && createPortal(
        <div
          ref={menuRef}
          style={{
            position: "fixed",
            top:      coords.top,
            right:    coords.right,
            transform: coords.placement === "up" ? "translateY(-100%)" : undefined,
          }}
          className="z-50 w-48 overflow-hidden rounded-control border border-border bg-app-panel shadow-lg"
        >
          {m.status !== "disabled" && (
            <MenuItem onClick={() => { setOpen(false); onResetPassword() }}>
              Reset password
            </MenuItem>
          )}
          <MenuItem onClick={() => { setOpen(false); onSoftRemove() }}>
            Remove
          </MenuItem>
        </div>,
        document.body,
      )}
    </>
  )
}

function MenuItem({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick}
      className="block w-full px-3 py-2 text-left text-sm text-app-text hover:bg-white/5">
      {children}
    </button>
  )
}

// --- Pagination --------------------------------------------------------

function Pagination({ pagination, onPageChange }: { pagination: TeamPagination; onPageChange: (p: number) => void }) {
  const { page, total, total_pages, from, to } = pagination
  if (total === 0) return null

  const pages = pageRange(page, total_pages)

  return (
    <div className="flex items-center justify-between text-sm text-app-muted">
      <div>Showing {from} to {to} of {total} users</div>
      <div className="flex items-center gap-1">
        <PageButton disabled={page <= 1} onClick={() => onPageChange(page - 1)} aria-label="Previous">
          <ChevronLeft size={14} />
        </PageButton>
        {pages.map((p, i) =>
          p === "…"
            ? <span key={`ellipsis-${i}`} className="px-2 text-app-subtle">…</span>
            : (
              <PageButton key={p} active={p === page} onClick={() => onPageChange(p)}>
                {p}
              </PageButton>
            ),
        )}
        <PageButton disabled={page >= total_pages} onClick={() => onPageChange(page + 1)} aria-label="Next">
          <ChevronRight size={14} />
        </PageButton>
      </div>
    </div>
  )
}

function PageButton({ children, active, disabled, onClick, ...rest }: {
  children: React.ReactNode
  active?: boolean
  disabled?: boolean
  onClick?: () => void
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "inline-flex h-8 min-w-[32px] items-center justify-center rounded-control border px-2 text-sm transition",
        active
          ? "border-brand-red bg-brand-redSoft text-brand-red"
          : "border-border bg-white/[0.03] text-app-text hover:bg-white/10",
        disabled && "cursor-not-allowed opacity-40 hover:bg-white/[0.03]",
      )}
      {...rest}
    >
      {children}
    </button>
  )
}

function pageRange(page: number, total: number): (number | "…")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
  const out: (number | "…")[] = [ 1 ]
  if (page > 3) out.push("…")
  for (let p = Math.max(2, page - 1); p <= Math.min(total - 1, page + 1); p++) out.push(p)
  if (page < total - 2) out.push("…")
  out.push(total)
  return out
}
